
import React, { useState, useEffect } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip, RadialBar, RadialBarChart } from 'recharts';
import { User, Users, Briefcase, Car, TrendingUp, TrendingDown, Filter, ArrowUpRight, ArrowDownRight, Home, Building2, MapPin } from 'lucide-react';

const CustomerSegments = ({ segmentId, selectedMetric, setSelectedMetric }) => {
  const [animationActive, setAnimationActive] = useState(false);
  
  useEffect(() => {
    // Trigger animation when component mounts or when segment changes
    setAnimationActive(true);
    const timer = setTimeout(() => setAnimationActive(false), 1000);
    return () => clearTimeout(timer);
  }, [segmentId]);

  // Define segment-specific customer segment data with new KPIs
  const segmentCustomerData = {
    urban: [
      { name: 'Local Residential', value: 30, color: '#8B5CF6', trend: 'up', transactionShare: 25, valueShare: 32, churnRate: 5.2, icon: 'home' },
      { name: 'Commercial', value: 45, color: '#3B82F6', trend: 'up', transactionShare: 50, valueShare: 48, churnRate: 3.8, icon: 'business' },
      { name: 'Commuters', value: 25, color: '#10B981', trend: 'down', transactionShare: 25, valueShare: 20, churnRate: 7.5, icon: 'car' }
    ],
    highway: [
      { name: 'Local Residential', value: 15, color: '#8B5CF6', trend: 'down', transactionShare: 12, valueShare: 18, churnRate: 8.3, icon: 'home' },
      { name: 'Commercial', value: 60, color: '#3B82F6', trend: 'up', transactionShare: 65, valueShare: 62, churnRate: 4.2, icon: 'business' },
      { name: 'Travelers', value: 25, color: '#F59E0B', trend: 'up', transactionShare: 23, valueShare: 20, churnRate: 6.8, icon: 'car' }
    ],
    rural: [
      { name: 'Local Residential', value: 55, color: '#8B5CF6', trend: 'up', transactionShare: 50, valueShare: 52, churnRate: 4.5, icon: 'home' },
      { name: 'Agricultural', value: 35, color: '#10B981', trend: 'down', transactionShare: 40, valueShare: 38, churnRate: 3.2, icon: 'business' },
      { name: 'Travelers', value: 10, color: '#F59E0B', trend: 'up', transactionShare: 10, valueShare: 10, churnRate: 9.4, icon: 'car' }
    ],
    suburban: [
      { name: 'Local Residential', value: 50, color: '#8B5CF6', trend: 'up', transactionShare: 45, valueShare: 48, churnRate: 5.8, icon: 'home' },
      { name: 'Commercial', value: 30, color: '#3B82F6', trend: 'down', transactionShare: 35, valueShare: 32, churnRate: 4.7, icon: 'business' },
      { name: 'Commuters', value: 20, color: '#10B981', trend: 'up', transactionShare: 20, valueShare: 20, churnRate: 6.2, icon: 'car' }
    ]
  };

  // Get the data for the selected segment
  const data = segmentCustomerData[segmentId] || segmentCustomerData.urban;
  
  // Create dataset based on selected KPI
  const getDataForMetric = () => {
    return data.map(item => ({
      ...item,
      value: selectedMetric === 'transactions' ? item.transactionShare :
             selectedMetric === 'value' ? item.valueShare :
             item.churnRate
    }));
  };
  
  // Get segment name for title
  const getSegmentName = () => {
    const names = {
      urban: 'Urban Premium',
      highway: 'Highway Transit',
      rural: 'Rural Standard',
      suburban: 'Suburban Mixed'
    };
    return names[segmentId] || 'Urban Premium';
  };
  
  // Format data for chart
  const chartData = getDataForMetric();
  
  // Get icon based on customer type
  const getSegmentIcon = (iconType) => {
    switch (iconType) {
      case 'home':
        return <Home size={16} />;
      case 'business':
        return <Building2 size={16} />;
      case 'car':
        return <Car size={16} />;
      default:
        return <Users size={16} />;
    }
  };

  const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, index, name, value }) => {
    const RADIAN = Math.PI / 180;
    const radius = outerRadius * 1.15;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    
    return (
      <text 
        x={x} 
        y={y} 
        fill={chartData[index].color}
        textAnchor={x > cx ? 'start' : 'end'} 
        dominantBaseline="central"
        className="text-xs font-medium"
      >
        {`${name} (${value}%)`}
      </text>
    );
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white p-4 border border-gray-100 rounded-lg shadow-lg text-sm">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-4 h-4 rounded-full" style={{ backgroundColor: data.color }}></div>
            <p className="font-semibold text-gray-900">{data.name}</p>
          </div>
          <div className="space-y-1">
            <p className="text-gray-700">
              {selectedMetric === 'transactions' ? 'Transaction Share: ' :
               selectedMetric === 'value' ? 'Est. Value Share: ' :
               'Customer Churn: '}
              <span className="font-semibold">
                {selectedMetric === 'churn' ? `${data.value}%` : `${data.value}%`}
              </span>
            </p>
            <div className="flex items-center mt-1 text-xs">
              <span className={`px-2 py-1 rounded-full flex items-center gap-1 ${
                data.trend === 'up' ? (selectedMetric === 'churn' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700') : 
                (selectedMetric === 'churn' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700')
              }`}>
                {data.trend === 'up' ? (
                  <ArrowUpRight size={12} className="inline" />
                ) : (
                  <ArrowDownRight size={12} className="inline" />
                )}
                {data.trend === 'up' ? '+3.2' : '-1.5'}% vs prev. period
              </span>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  // Animated chart properties for RadialBar
  const animatedData = chartData.map(item => ({
    ...item,
    value: animationActive ? item.value * 0.1 : item.value
  }));

  return (
    <div className="bg-white rounded-xl shadow-sm p-6 transition-all hover:shadow-md border border-gray-100">
      <div className="mb-5 flex justify-between items-center">
        <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
          <Users size={20} className="text-purple-600" />
          Customer Segments - {getSegmentName()}
        </h3>
        <div className="flex items-center px-3 py-2 bg-gray-50 rounded-full">
          <Filter size={14} className="mr-2 text-gray-500" />
          <select 
            className="text-sm bg-transparent border-none outline-none pr-1"
            value={selectedMetric}
            onChange={(e) => setSelectedMetric(e.target.value)}
          >
            <option value="transactions">No. of Transactions Share</option>
            <option value="value">Est. Value Share</option>
            <option value="churn">Customer Churn %</option>
          </select>
        </div>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="lg:col-span-2">
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <defs>
                  {chartData.map((entry, index) => (
                    <linearGradient key={`gradient-${index}`} id={`colorGradient-${index}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={entry.color} stopOpacity={0.8}/>
                      <stop offset="100%" stopColor={entry.color} stopOpacity={1}/>
                    </linearGradient>
                  ))}
                </defs>
                <Pie
                  data={chartData}
                  cx="50%"
                  cy="50%"
                  labelLine={true}
                  label={renderCustomizedLabel}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                  animationDuration={800}
                  animationBegin={0}
                  animationEasing="ease-out"
                  stroke="#fff"
                  strokeWidth={2}
                >
                  {chartData.map((entry, index) => (
                    <Cell 
                      key={`cell-${index}`} 
                      fill={`url(#colorGradient-${index})`} 
                      className="hover:opacity-80 transition-opacity"
                    />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
        
        <div className="lg:col-span-3">
          <div className="space-y-4">
            {data.map((segment, index) => (
              <div 
                key={index} 
                className="bg-gray-50 rounded-lg p-4 hover:shadow-sm transition-all border border-gray-100"
              >
                <div className="flex justify-between items-center mb-2">
                  <div className="flex items-center gap-2">
                    <div className="p-2 rounded-lg" style={{ backgroundColor: `${segment.color}25` }}>
                      {getSegmentIcon(segment.icon)}
                    </div>
                    <span className="font-medium text-gray-900">{segment.name}</span>
                  </div>
                  <div className={`flex items-center text-xs px-2 py-1 rounded-full ${
                    segment.trend === 'up' ? 'text-green-700 bg-green-50' : 'text-red-700 bg-red-50'
                  }`}>
                    {segment.trend === 'up' ? (
                      <TrendingUp size={12} className="mr-1" />
                    ) : (
                      <TrendingDown size={12} className="mr-1" />
                    )}
                    {segment.trend === 'up' ? '+' : '-'}{segment.trend === 'up' ? '3.2' : '1.5'}%
                  </div>
                </div>
                
                <p className="text-xs text-gray-600 mb-3">
                  {segment.name === 'Local Residential' && 
                    'Local residents who live nearby and visit regularly'}
                  {segment.name === 'Commercial' && 
                    'Business customers with fleet accounts or expense cards'}
                  {segment.name === 'Commuters' && 
                    'Daily travelers with regular purchase patterns'}
                  {segment.name === 'Travelers' && 
                    'Long-distance travelers with occasional visits'}
                  {segment.name === 'Agricultural' && 
                    'Farming businesses with seasonal fuel needs'}
                </p>
                
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500 w-16">Trnx Share:</span>
                    <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div 
                        className="h-full rounded-full transition-all duration-1000 ease-out" 
                        style={{ 
                          width: `${segment.transactionShare}%`, 
                          backgroundColor: segment.color,
                          opacity: selectedMetric === 'transactions' ? 1 : 0.6 
                        }}
                      ></div>
                    </div>
                    <span className="text-xs font-medium w-8 text-right">{segment.transactionShare}%</span>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500 w-16">Value Share:</span>
                    <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div 
                        className="h-full rounded-full transition-all duration-1000 ease-out" 
                        style={{ 
                          width: `${segment.valueShare}%`, 
                          backgroundColor: segment.color,
                          opacity: selectedMetric === 'value' ? 1 : 0.6 
                        }}
                      ></div>
                    </div>
                    <span className="text-xs font-medium w-8 text-right">{segment.valueShare}%</span>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500 w-16">Churn Rate:</span>
                    <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div 
                        className="h-full rounded-full transition-all duration-1000 ease-out" 
                        style={{ 
                          width: `${segment.churnRate * 5}%`, // Multiply by 5 to make the bar visible since churn values are smaller 
                          backgroundColor: segment.color,
                          opacity: selectedMetric === 'churn' ? 1 : 0.6 
                        }}
                      ></div>
                    </div>
                    <span className="text-xs font-medium w-8 text-right">{segment.churnRate}%</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerSegments;
